/**
 * The portal Work dialog's policy: its model from the Work task read, and which blocks it shows. Pure, so the
 * dialog's state rules (no Job, has a Job, resolved) are tested without mounting it.
 */
import { ServiceHistoryCompletionStatus, WorkTaskScheduleTask, WorkTaskScheduleTaskVisit } from '@jms/model';
import { DateTime } from 'luxon';
import { WorkTaskOccurrenceModel } from '../work.task.occurrence.model';
import { frequencyLabel } from './occurrence.frequency.label';
import { visitWhen } from './work.task.detail';
import { workTaskOccurrenceKey } from './work.task.occurrence.key';

/** Completion states that close the work, so the dialog shows its Result: done or skipped, not still open or removed. */
const RESOLVED: ReadonlySet<ServiceHistoryCompletionStatus | undefined> = new Set([ServiceHistoryCompletionStatus.Resolved, ServiceHistoryCompletionStatus.Skipped]);

/** Prefix of a sibling key that no occurrence read can answer, so opening it does nothing. */
const UNRESOLVABLE_SIBLING = 'sibling:';

/**
 * The dialog's model for one task read. `key` is the record key the dialog was opened by; siblings get keys of
 * their own when they can be read back, which is only before a Job exists: a Job's tasks carry the Job's ids,
 * not the plan task ids the Work read is keyed by.
 */
export function workTaskOccurrenceModel(task: WorkTaskScheduleTask, key: string): WorkTaskOccurrenceModel {

  const row = task.row;
  const visit = task.visit;
  const jobLink = [row.job, visit?.job].find(job => !!job?._id) ?? null;

  return {
    ...row,
    key,
    visitDate: row.rescheduledDate ?? row.date,
    frequencyLabel: frequencyLabel(row.frequencyDescription) ?? row.frequency ?? null,
    description: task.description || null,
    startsOn: task.startsOn ?? null,
    duration: task.duration ?? null,
    lastCompleted: task.lastCompleted ?? null,
    projectedUntil: task.projectedUntil ?? null,
    subtasks: task.subtasks,
    siblings: task.siblings.map(sibling => ({
      key: (jobLink ? null : workTaskOccurrenceKey({ taskId: sibling._id, servicePlan: row.servicePlan, date: row.date })) ?? `${UNRESOLVABLE_SIBLING}${sibling._id}`,
      name: sibling.name ?? '',
      frequency: null,
    })),
    appointment: jobLink && visit ? appointment(visit) : null,
    crew: jobLink ? (visit?.jobSummary?.crew ?? visit?.resources ?? []).filter(resource => !!resource?._id) : [],
    jobStatus: jobLink ? visit?.displayStatus ?? null : null,
    jobLink,
    outcome: RESOLVED.has(row.completionStatus) ? row.resultLabel ?? row.resolution?.name ?? row.completionStatus ?? null : null,
    resolved: RESOLVED.has(row.completionStatus),
  };
}

/** Whether a sibling's key opens an occurrence; a Job's task cannot be read back by the Work task read. */
export function isOpenableSiblingKey(key: string): boolean {

  return !key.startsWith(UNRESOLVABLE_SIBLING);
}

/** The Job's booked start in the Visit's zone, else the Visit's own day and window. */
function appointment(visit: WorkTaskScheduleTaskVisit): string {

  const booked = visit.jobSummary?.appointmentAt;
  if (!booked) return visitWhen(visit);
  const start = DateTime.fromJSDate(new Date(booked), { zone: visit.timezone });

  return start.isValid ? start.toFormat('ccc d LLL yyyy · HH:mm') : visitWhen(visit);
}
